/**
 * AutoPilot (BaseFareOverride) Mixpanel property builders (RM-1228).
 *
 * One pure, stage-oriented module owns the whole AutoPilot analytics vocabulary: each public
 * builder accepts the camelCase stage facts its tracker already holds and returns only the
 * properties that event's contract allows. Builders never read Redux, navigation state, tenant
 * configuration, or caches, never mutate their inputs, and answer malformed or conflicting data
 * with omission — an empty object simply adds nothing to the base event.
 */

import {
  isBfoPricing,
  isBfoPricingContext,
  isBfoReturnedPurchase,
} from 'utils/purchase/baseFareOverride';

const DECIMAL_NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

/**
 * Finite number or undefined. The backend serializes AutoPilot money and percentages as decimal
 * strings (`'450.00'`), so every numeric read goes through here before it is emitted or summed.
 * @param {*} value - Raw value.
 * @returns {number|undefined} Finite number, or undefined when the value is not numeric.
 */
const toFiniteNumber = (value) => {
  let number;

  if (typeof value === 'number') number = value;
  else if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!DECIMAL_NUMBER_PATTERN.test(trimmedValue)) return undefined;
    number = Number(trimmedValue);
  } else return undefined;

  return Number.isFinite(number) ? number : undefined;
};

/**
 * Cent-rounded amount, so decimal-string sources and float addition cannot leak
 * `0.30000000000000004` into a Mixpanel money property.
 * @param {number} value - Finite amount.
 * @returns {number|undefined} Amount rounded to two decimals, or undefined on overflow.
 */
const roundMoney = (value) => {
  if (!Number.isFinite(value)) return undefined;
  const roundedValue = Math.round(value * 100) / 100;
  return Number.isFinite(roundedValue) ? roundedValue : undefined;
};

/**
 * `{ [name]: value }` when the value is present, `{}` otherwise. Keeps the "omit, never
 * placeholder" rule readable at every call site.
 * @param {string} name - Mixpanel property name.
 * @param {*} value - Candidate value.
 * @returns {object} Single-entry object, or an empty one.
 */
const prop = (name, value) => (value === undefined ? {} : { [name]: value });

/**
 * Whether a stage fact is a non-array object whose named fields can be trusted as one record.
 * @param {*} value - Candidate record.
 * @returns {boolean} True for a non-null, non-array object.
 */
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * The one non-empty string every governed context supplies, or undefined otherwise.
 *
 * A missing, empty, or wrong-type candidate is disagreement, not abstention: `[undefined,
 * 'decrease']` must not publish `'decrease'` — the direction of the silent context is unknown.
 * @param {Array<*>} values - Candidate values, one per governed context.
 * @returns {string|undefined} The shared string, or undefined.
 */
const agreedString = (values) => {
  if (values.length === 0) return undefined;
  const [first] = values;
  if (typeof first !== 'string' || first === '') return undefined;
  return values.every((value) => value === first) ? first : undefined;
};

/**
 * The one finite number every governed context normalizes to, or undefined otherwise.
 * @param {Array<*>} values - Candidate values, one per governed context.
 * @returns {number|undefined} The shared finite number, or undefined.
 */
const agreedNumber = (values) => {
  if (values.length === 0) return undefined;
  const normalized = values.map(toFiniteNumber);
  const [first] = normalized;
  if (first === undefined) return undefined;
  return normalized.every((value) => value === first) ? first : undefined;
};

/**
 * Dimensions and provenance shared by every AutoPilot-enriched event, evaluated independently
 * per property over all canonical governed contexts at the stage.
 *
 * Only the three required provenance IDs plus the fare group are emitted: `sourceBaseFareId`
 * and `sourceTripBaseFareId` are optional on the backend, and a nullable join key is worse
 * than no join key.
 * @param {Array<object>} contexts - Canonical governed pricing contexts.
 * @returns {object} Mixpanel properties.
 */
const buildSharedContextProps = (contexts) => {
  /**
   * Agreed non-empty string across every governed context for one field.
   * @param {string} key - Context field name.
   * @returns {string|undefined} The shared string, or undefined.
   */
  const agreedStr = (key) => agreedString(contexts.map((context) => context?.[key]));

  /**
   * Agreed finite number across every governed context for one field.
   * @param {string} key - Context field name.
   * @returns {number|undefined} The shared finite number, or undefined.
   */
  const agreedNum = (key) => agreedNumber(contexts.map((context) => context?.[key]));

  return {
    'AutoPilot Applied': true,
    ...prop('AutoPilot Adjustment Direction', agreedStr('adjustmentDirection')),
    ...prop('AutoPilot Scope Type', agreedStr('scopeType')),
    ...prop('AutoPilot Adjustment Percent', agreedNum('providerAdjustmentPercent')),
    ...prop('AutoPilot Override ID', agreedNum('sourceBaseFareOverrideId')),
    ...prop('AutoPilot Recommendation ID', agreedNum('sourceRecommendationId')),
    ...prop('AutoPilot Trip Fare ID', agreedNum('sourceTripFareId')),
    ...prop('AutoPilot Fare Group ID', agreedStr('fareGroupId')),
  };
};

/**
 * AutoPilot properties for `Picked Departure` / `Picked Return`, derived from the exact pricing
 * block the selected result card rendered. A trip can be AutoPilot-priced on `roundTripPricing`
 * and not on `pricing`, so only the rendered block may classify the selection.
 * @param {object} params - Stage facts.
 * @param {object} [params.pricing] - Pricing block the card actually rendered.
 * @param {string} [params.way] - Existing lower-case `departure` / `return` direction.
 * @returns {object} Mixpanel properties, or `{}` when the rendered block is not canonical.
 */
export function buildAutopilotDisplayedTripProps({ pricing, way } = {}) {
  if (!isBfoPricing(pricing)) return {};

  return {
    ...buildSharedContextProps([pricing.pricingContext]),
    ...prop('Way', way),
  };
}

/**
 * Whether a seat carries a backend quote. The nullish check mirrors
 * `resolveBfoEffectiveSeatPrice`: numeric strings and zero survive.
 * @param {object} [seat] - Seat object.
 * @returns {boolean} True when the seat has a per-seat quote.
 */
const hasSeatQuote = (seat) => seat?.dynamicPrice != null;

/**
 * Cent-rounded finite amount, or undefined when the source is unreadable.
 * @param {*} value - Raw amount.
 * @returns {number|undefined} Rounded amount, or undefined.
 */
const toMoney = (value) => {
  const amount = toFiniteNumber(value);
  return amount === undefined ? undefined : roundMoney(amount);
};

/**
 * AutoPilot properties for `View Seats`, from the displayed leg pricing and the seat map the
 * customer observes. The quoted-seat count is what makes a scoped offer legible: under
 * `all_seats` it equals the whole map, under a scoped offer it is the eligible subset.
 *
 * Entries without a `number` are not seats — the keyed map carries sibling keys such as
 * `categories`.
 * @param {object} params - Stage facts.
 * @param {object} [params.pricing] - Displayed leg pricing block.
 * @param {object|Array} [params.seats] - Seat map (`{ '4': seat }`) or seat array.
 * @returns {object} Mixpanel properties, or `{}` when the leg pricing is not canonical.
 */
export function buildAutopilotSeatMapProps({ pricing, seats } = {}) {
  if (!isBfoPricing(pricing)) return {};

  const sharedProps = buildSharedContextProps([pricing.pricingContext]);
  if (!Array.isArray(seats) && !isRecord(seats)) return sharedProps;

  const seatList = Array.isArray(seats) ? seats : Object.values(seats);
  const quotedSeatCount = seatList.filter(
    (seat) => isRecord(seat) && seat.number != null && hasSeatQuote(seat),
  ).length;

  return {
    ...sharedProps,
    'AutoPilot Quoted Seat Count': quotedSeatCount,
  };
}

/**
 * AutoPilot properties for `Seat Tapped`, from the displayed leg pricing and the tapped seat.
 *
 * Gated on the leg pricing, never on the seat alone: legacy urgency-discount trips also carry
 * per-seat `dynamicPrice` quotes, so a quote by itself must not label a seat AutoPilot. A
 * quoted seat's effective amount is its backend quote; an unquoted seat reports its provider
 * fare for both amounts. Each amount is omitted independently when its own source is
 * unreadable — never fabricated as zero. The existing `Fare amount` property is untouched, and
 * no `Way` is added.
 * @param {object} params - Stage facts.
 * @param {object} [params.pricing] - Displayed leg pricing block.
 * @param {object} [params.seat] - Tapped seat.
 * @returns {object} Mixpanel properties, or `{}` when the leg pricing is not canonical.
 */
export function buildAutopilotTappedSeatProps({ pricing, seat } = {}) {
  if (!isBfoPricing(pricing)) return {};

  const sharedProps = buildSharedContextProps([pricing.pricingContext]);
  if (!isRecord(seat) || seat.number == null) return sharedProps;

  const quotePresent = hasSeatQuote(seat);
  const providerAmount = toMoney(seat.price);
  const effectiveAmount = quotePresent ? toMoney(seat.dynamicPrice) : providerAmount;

  return {
    ...sharedProps,
    'AutoPilot Seat Quote Present': quotePresent,
    ...prop('AutoPilot Provider Fare Amount', providerAmount),
    ...prop('AutoPilot Effective Fare Amount', effectiveAmount),
  };
}

/**
 * The three money properties summed over per-item amounts — or nothing at all when any governed
 * item lacks a readable amount. Zero-filling a missing amount would invent a price: the item
 * would still raise `AutoPilot Ticket Count` while contributing `0` to a published total. A
 * valid empty governed set sums to a zero trio; it is not malformed data.
 * Sources remain unrounded until after summation so sub-cent item precision is preserved.
 * @param {Array<number|undefined>} providerAmounts - Finite provider amount per governed item.
 * @param {Array<number|undefined>} effectiveAmounts - Finite effective amount per governed item.
 * @returns {object} The three money properties, or an empty object.
 */
const buildMoneyTotals = (providerAmounts, effectiveAmounts) => {
  if ([...providerAmounts, ...effectiveAmounts].some((value) => value === undefined)) return {};

  const providerTotal = roundMoney(providerAmounts.reduce((total, value) => total + value, 0));
  const effectiveTotal = roundMoney(effectiveAmounts.reduce((total, value) => total + value, 0));
  const adjustmentAmount =
    providerTotal === undefined || effectiveTotal === undefined
      ? undefined
      : roundMoney(effectiveTotal - providerTotal);

  if (
    providerTotal === undefined ||
    effectiveTotal === undefined ||
    adjustmentAmount === undefined
  ) {
    return {};
  }

  return {
    'AutoPilot Provider Base Total': providerTotal,
    'AutoPilot Effective Base Total': effectiveTotal,
    'AutoPilot Adjustment Amount': adjustmentAmount,
  };
};

/**
 * AutoPilot properties for `Continue With Seats`, summed over the passenger seats the customer
 * selected (adjacent-companion seats are excluded by the caller).
 *
 * Only quoted seats are governed: under a scoped offer a selection is routinely a mix of
 * eligible and regular seats, and summing the regular ones into an AutoPilot total would invent
 * revenue AutoPilot never touched. A governed seat whose provider fare or quote is unreadable
 * silences the money trio atomically while the counts stay.
 * @param {object} params - Stage facts.
 * @param {object} [params.pricing] - Displayed leg pricing block.
 * @param {Array<object>} [params.selectedSeats] - Selected non-companion passenger seats.
 * @returns {object} Mixpanel properties, or `{}` when the leg pricing is not canonical.
 */
export function buildAutopilotSelectedSeatsProps({ pricing, selectedSeats } = {}) {
  if (!isBfoPricing(pricing)) return {};

  const sharedProps = buildSharedContextProps([pricing.pricingContext]);
  if (!Array.isArray(selectedSeats)) return sharedProps;
  if (!selectedSeats.every((seat) => isRecord(seat) && seat.number != null)) return sharedProps;

  const quotedSeats = selectedSeats.filter((seat) => hasSeatQuote(seat));

  return {
    ...sharedProps,
    'AutoPilot Ticket Count': quotedSeats.length,
    'AutoPilot Regular Ticket Count': selectedSeats.length - quotedSeats.length,
    ...buildMoneyTotals(
      quotedSeats.map((seat) => toFiniteNumber(seat?.price)),
      quotedSeats.map((seat) => toFiniteNumber(seat?.dynamicPrice)),
    ),
  };
}

/**
 * AutoPilot result counts for `View Results`, the exposure denominator.
 *
 * One direct linear pass per direction, run only while this event is prepared. A round-trip
 * departure result is read through `departureRoundTripPricing` when non-null and `pricing`
 * otherwise; a return result through `roundTripPricing` with the same fallback — the approved
 * context-free exposure rule.
 *
 * Both counts are emitted together, or neither is: a search with AutoPilot on the return leg
 * only needs `AutoPilot Departure Results: 0` to be readable as "none on the way out", while a
 * pair of zeros on every ordinary search would fill the property with noise. Read the counts
 * as availability bounds, not impressions: return counts arrive while the customer is still on
 * the departure list, and partial-poll counts remain possible.
 * @param {object} params - Stage facts.
 * @param {Array<object>} [params.departureTrips] - Departure result trips.
 * @param {Array<object>} [params.returnTrips] - Return result trips; omit on one-way searches.
 * @returns {object} Mixpanel properties, or `{}` when no applicable result is AutoPilot-priced.
 */
export function buildAutopilotResultCountProps({ departureTrips, returnTrips } = {}) {
  /**
   * Canonical results in one list through the approved context-free fallback rule.
   * @param {Array<object>} trips - Result trips for one direction.
   * @param {string} roundTripKey - Round-trip pricing key preferred for that direction.
   * @returns {number|undefined} Applicable canonical result count, or undefined for a malformed list.
   */
  const countApplicable = (trips, roundTripKey) => {
    if (!Array.isArray(trips)) return undefined;
    return trips.filter((trip) => isBfoPricing(trip?.[roundTripKey] ?? trip?.pricing)).length;
  };

  const departureCount = countApplicable(departureTrips, 'departureRoundTripPricing');
  const returnCount = Array.isArray(returnTrips)
    ? countApplicable(returnTrips, 'roundTripPricing')
    : undefined;

  if ((departureCount ?? 0) === 0 && (returnCount ?? 0) === 0) return {};

  return {
    ...prop('AutoPilot Departure Results', departureCount),
    ...prop('AutoPilot Return Results', returnCount),
  };
}

/**
 * AutoPilot properties for `Passengers Created` / `Confirm Itinerary`, from the API-returned
 * tickets of the current purchase — never from search navigation state, provisional selections,
 * or cached attribution.
 *
 * A governed ticket carries the canonical context at its root; its provider amount comes from
 * that context and its effective amount from the authoritative returned ticket total. Regular
 * tickets contribute only to `AutoPilot Regular Ticket Count`. When the collection arrives as
 * explicit leg groups, `AutoPilot Applied Leg Count` reports how many legs are governed; a flat
 * collection omits the count rather than guessing leg ownership.
 * @param {object} params - Stage facts.
 * @param {Array} [params.legTicketGroups] - Returned tickets, one group per leg.
 * @returns {object} Mixpanel properties, or `{}` when no returned ticket is canonical.
 */
export function buildAutopilotTicketProps({ legTicketGroups } = {}) {
  if (!Array.isArray(legTicketGroups)) return {};

  const hasLegIdentity =
    legTicketGroups.length > 0 && legTicketGroups.every((group) => Array.isArray(group));
  const ticketCandidates = hasLegIdentity ? legTicketGroups.flat() : legTicketGroups;

  /**
   * A non-empty record is the minimum trustworthy shape for one API-returned ticket. Arrays and
   * empty placeholders cannot be counted as either governed or regular tickets.
   * @param {*} ticket - Candidate returned ticket row.
   * @returns {boolean} True when the candidate is a recognizable ticket record.
   */
  const isTicketRecord = (ticket) => isRecord(ticket) && Object.keys(ticket).length > 0;
  const collectionIsValid = ticketCandidates.every(isTicketRecord);
  const tickets = ticketCandidates.filter(isTicketRecord);

  /**
   * Whether one ticket row is governed by the canonical AutoPilot context at its root.
   * @param {object} ticket - Returned ticket row.
   * @returns {boolean} True when the ticket context is canonical.
   */
  const isGovernedTicket = (ticket) => isBfoPricingContext(ticket.pricingContext);
  const governedTickets = tickets.filter(isGovernedTicket);
  if (governedTickets.length === 0) return {};

  /**
   * Whether one explicit leg group contains at least one governed ticket.
   * @param {Array} group - Ticket rows for one leg.
   * @returns {boolean} True when the leg is governed.
   */
  const legHasGovernedTicket = (group) =>
    group.some((ticket) => isTicketRecord(ticket) && isGovernedTicket(ticket));
  const appliedLegCount = hasLegIdentity
    ? legTicketGroups.filter(legHasGovernedTicket).length
    : undefined;
  const providerAmounts = governedTickets.map((ticket) => {
    return toFiniteNumber(ticket.pricingContext.providerBasePrice);
  });
  const effectiveAmounts = governedTickets.map((ticket) => toFiniteNumber(ticket.total));

  return {
    ...buildSharedContextProps(governedTickets.map((ticket) => ticket.pricingContext)),
    ...(collectionIsValid
      ? {
          ...prop('AutoPilot Applied Leg Count', appliedLegCount),
          'AutoPilot Ticket Count': governedTickets.length,
          'AutoPilot Regular Ticket Count': tickets.length - governedTickets.length,
          ...buildMoneyTotals(providerAmounts, effectiveAmounts),
        }
      : {}),
  };
}

/**
 * AutoPilot properties for `Purchase Attempt`, from the charged pricing of the currently
 * returned purchase fragments.
 *
 * Tickets do not exist at this stage, so only dimensions, provenance, and the applied-leg
 * count are emitted: a per-passenger leg fare is not a ticket total, and publishing it under a
 * "Total" property would give that property two meanings across the funnel.
 *
 * When the collection arrives as explicit leg groups (`[departurePricings, returnPricings]`),
 * `AutoPilot Applied Leg Count` reports how many legs the override governs. A flat collection or
 * an invalid expected-leg placeholder omits the count rather than guessing leg ownership.
 * @param {object} params - Stage facts.
 * @param {Array} [params.legPricingGroups] - Charged pricing blocks, one group per leg.
 * @returns {object} Mixpanel properties, or `{}` when no charged pricing is canonical.
 */
export function buildAutopilotPurchaseFragmentProps({ legPricingGroups } = {}) {
  if (!Array.isArray(legPricingGroups)) return {};

  const hasLegIdentity =
    legPricingGroups.length > 0 && legPricingGroups.every((group) => Array.isArray(group));
  const pricingBlocks = hasLegIdentity ? legPricingGroups.flat() : legPricingGroups;
  const collectionIsValid = pricingBlocks.every(
    (pricing) => isRecord(pricing) && Object.keys(pricing).length > 0,
  );

  const governedBlocks = pricingBlocks.filter((pricing) => isBfoPricing(pricing));
  if (governedBlocks.length === 0) return {};

  const appliedLegCount = hasLegIdentity
    ? legPricingGroups.filter((group) => group.some((pricing) => isBfoPricing(pricing))).length
    : undefined;

  return {
    ...buildSharedContextProps(governedBlocks.map((pricing) => pricing.pricingContext)),
    ...(collectionIsValid ? prop('AutoPilot Applied Leg Count', appliedLegCount) : {}),
  };
}

/**
 * Properties for the accepted `Price Change Modal` `Shown` observation.
 *
 * `newTotal` and `formerTotal` must be the exact values the rendered modal already displays;
 * they are normalized only to finite, cent-rounded Mixpanel numbers. `Price Lower` applies the
 * modal's existing lower-price rule to the finite totals and is omitted with either unreadable
 * total. Only AutoPilot classification comes from the currently returned purchase — never from
 * provisional or cached context — and no other shared AutoPilot property is added.
 * @param {object} params - Stage facts.
 * @param {*} [params.newTotal] - New total the modal displays.
 * @param {*} [params.formerTotal] - Former total the modal displays.
 * @param {object} [params.purchase] - Currently returned purchase state.
 * @returns {object} Mixpanel properties for the `Shown` observation.
 */
export function buildPriceChangeShownProps({ newTotal, formerTotal, purchase } = {}) {
  const newAmount = toFiniteNumber(newTotal);
  const formerAmount = toFiniteNumber(formerTotal);
  const roundedNewAmount = newAmount === undefined ? undefined : roundMoney(newAmount);
  const roundedFormerAmount = formerAmount === undefined ? undefined : roundMoney(formerAmount);

  return {
    ...prop('New Total', roundedNewAmount),
    ...prop('Former Total', roundedFormerAmount),
    ...(roundedNewAmount !== undefined && roundedFormerAmount !== undefined
      ? { 'Price Lower': newAmount < formerAmount }
      : {}),
    ...(isBfoReturnedPurchase(purchase) ? { 'AutoPilot Applied': true } : {}),
  };
}
